import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { PageHeader } from '~/components/Navigation/PageHeader'
import { StalenessIndicator } from '~/components/Navigation/StalenessIndicator'
import { DimFilterPanel } from '~/components/Filters/DimFilterPanel'
import { TimeRangePicker } from '~/components/Filters/TimeRangePicker'
import { KpiCascadeTrigger } from '~/components/Filters/KpiCascadeTrigger'
import {
  TimeframeSelect,
  toAbsoluteTimeframes,
  toSurfacedRollingTimeframes,
} from '~/components/Filters/TimeframeSelect'
import { GranularityControls } from '~/components/Filters/GranularityControls'
import { ResetSettingsButton } from '~/components/Filters/ResetSettingsButton'
import { MultiTimeframePicker } from '~/components/Filters/MultiTimeframePicker'
import { ComparisonSwitch } from './ComparisonSwitch'
import { CoarseningBadge } from './CoarseningBadge'
import { ControlBand, ControlZone } from './ControlBand'
import { shouldShowPanelBase } from './showPanelBase'
import { PanelBaseCheckbox } from './PanelBaseCheckbox'
import { usePageControls } from '~/api/hooks/useCharts'
import { useSyncModelFloor } from '~/state/useSyncModelFloor'
import { useModelFloorIso } from '~/state/modelFloor'
import {
  buildCampaignSelectionParams,
  CAMPAIGN_MANAGED_DIMS,
  predicateOf,
} from '~/state/campaignPredicate'
import type { ReviewPageState } from './useReviewPageState'
import type { ComparisonState } from './useComparisonState'
import {
  MESSAGES,
  CONTROLS,
  prunedDimsNotice,
  campaignPrunedNotice,
  removedKpiNotice,
} from '~/components/Charts/chrome/chartChrome'

interface Props {
  title: string
  state: ReviewPageState
  compare?: ComparisonState
  showComparison?: boolean
  showGranularity?: boolean
  /** FU-f: page's registry tile set includes dynamics_panels. */
  panelBearing?: boolean
  /** Row 21: roots already rendered by the page's tiles (e.g. the Dynamics driver row);
   *  ComparisonSwitch excludes them from the metric-compare DEFAULT pick. */
  compareExcludedRoots?: string[]
  /** Page-specific control(s) rendered in the control row after the KPI cascade
   *  (e.g. the Contribution view toggle). */
  extraControls?: ReactNode
  children: ReactNode
}

export function ReviewPageScaffold({
  title,
  state,
  compare,
  showComparison = true,
  showGranularity = true,
  panelBearing = false,
  compareExcludedRoots,
  extraControls,
  children,
}: Props) {
  const controls = usePageControls(state.factTable)
  // Sync the model floor from get_controls into the global store (single mount point).
  useSyncModelFloor(controls.data)
  const floorIso = useModelFloorIso()
  // Keep only items with both bounds so they can drive a selection.
  const campaignItems = (controls.data?.campaign_timeslots?.items ?? []).filter(
    (i) => i.from && i.to,
  )
  const frameOptions = toAbsoluteTimeframes(controls.data?.timeframe_frames?.items)
  const rollingOptions = toSurfacedRollingTimeframes(controls.data?.timeframe_rolling?.items)

  // Row 15-light: the previous-period baseline checkbox shows only where it is APPLICABLE —
  // granularity visible, compare off (compare suppresses panel mode), the window actually
  // partitions into ≥2 panels, and no active multi-timeframe selection (per-TF panels).
  // FU-f (operator 2026-06-11: EXPOSE): panel-bearing pages (registry tile set includes
  // dynamics_panels) are exempt from the compare-suppression — sub-period panels and compare
  // rows legitimately coexist on Dynamics. ReviewKpis-class pages keep the hide.
  // An inapplicable URL-carried panel_base stays silently inert (state-level gate, unchanged).
  const multiTfActive = Boolean(state.multiTimeframe && state.multiTimeframe.entries.length > 0)
  const showPanelBase = shouldShowPanelBase({
    showGranularity,
    compareMode: compare ? compare.mode : null,
    panelBearing,
    dots: state.granularity.dots,
    windowBuckets: state.windowBuckets,
    multiTfActive,
  })

  // Wave-4 §1: row 2 carries presentation + conditionals (granularity, compare-period,
  // panel-base baseline, multi-TF) — it renders only when at least one is active.
  const showComparePeriod = Boolean(compare && showComparison && compare.mode === 'period')
  const showMultiTf = Boolean(state.multiTimeframe)
  const hasRow2 = showGranularity || showPanelBase || showComparePeriod || showMultiTf

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <PageHeader title={title} actions={<ResetSettingsButton />} />
      <Stack spacing={2}>
        {state.kpiCascade.controlsError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={state.kpiCascade.retryControls}>
                {MESSAGES.retry}
              </Button>
            }
          >
            {MESSAGES.controlsLoadFailed}
          </Alert>
        ) : state.kpiCascade.controlsEmpty ? (
          <Alert severity="warning">{MESSAGES.noKpisConfigured}</Alert>
        ) : null}
        <ControlBand
          row2={
            hasRow2 ? (
              <>
                {showGranularity ? (
                  <ControlZone testId="control-row2-granularity" flex={1}>
                    <GranularityControls
                      unit={state.granularity.unit}
                      dots={state.granularity.dots}
                      onUnitChange={state.granularity.setUnit}
                      onDotsChange={state.granularity.setDots}
                    />
                    <CoarseningBadge governance={state.governance} />
                  </ControlZone>
                ) : null}
                {showComparePeriod ? (
                  <ControlZone testId="control-row2-compareperiod">
                    <Typography variant="caption" color="text.secondary">
                      {CONTROLS.comparisonPeriod}
                    </Typography>
                    <TimeRangePicker
                      fromIso={compare!.fromIso}
                      toIso={compare!.toIso}
                      onChange={({ fromIso: f, toIso: t }) => compare!.setRange(f, t)}
                    />
                  </ControlZone>
                ) : null}
                {showPanelBase ? (
                  <ControlZone testId="control-row2-panelbase">
                    <PanelBaseCheckbox value={state.panelBase} onChange={state.setPanelBase} />
                  </ControlZone>
                ) : null}
                {showMultiTf ? (
                  // W5 gate-fix round 2: no zone caption — the picker's own floating
                  // "Add Timeframe" label carries the semantics (wave-4 D2 rule). The
                  // caption stacked above the picker on Dynamics' width-squeezed zone,
                  // re-growing row 2 to 80px.
                  <ControlZone testId="control-row2-multitf" flex={1}>
                    <MultiTimeframePicker
                      entries={state.multiTimeframe!.entries}
                      // Rolling frames intentionally excluded here — the multi-TF overlay is a
                      // parked surface (WP-C out of scope); only fixed frames + campaigns are
                      // offered. Campaign options carry their id + predicate flag (Task 6) so
                      // added entries round-trip the campaign id (Task 7 resolves per-arm).
                      options={[
                        ...frameOptions,
                        ...campaignItems.map((i) => ({
                          value: i.value,
                          label: i.label,
                          from: i.from!,
                          to: i.to!,
                          campaign: i.value,
                          hasPredicate: Object.keys(predicateOf(i)).length > 0,
                        })),
                      ]}
                      onAdd={state.multiTimeframe!.add}
                      onRemove={state.multiTimeframe!.remove}
                    />
                  </ControlZone>
                ) : null}
              </>
            ) : null
          }
        >
          <ControlZone testId="control-card-kpi">
            {/* W6 gate-fix round 2: the trigger owns the bare/trigger gate (and suspends
                it while its popover is open) — see KpiCascadeTrigger. */}
            <KpiCascadeTrigger state={state.kpiCascade} />
            {extraControls}
          </ControlZone>
          <ControlZone testId="control-card-timeframe" flex={1.4} minWidth={476}>
            <TimeframeSelect
              preset={state.timeframePreset.preset}
              fromIso={state.fromIso}
              toIso={state.toIso}
              frames={frameOptions}
              rolling={rollingOptions}
              campaignItems={campaignItems}
              onSelectPreset={state.timeframePreset.setPreset}
              onSelectAbsolute={(from, to) =>
                state.setMultipleSearchParams({ from, to, tf_preset: 'custom' })
              }
              onSelectCampaign={(item) =>
                state.setMultipleSearchParams(buildCampaignSelectionParams(item))
              }
              activeCampaignId={state.campaign?.id ?? null}
            />
            <TimeRangePicker
              fromIso={state.fromIso}
              toIso={state.toIso}
              minDate={floorIso ? new Date(floorIso) : undefined}
              onChange={({ fromIso: f, toIso: t }) =>
                // A manual date edit means a custom window — neutralize the active preset, else
                // its computedRange overrides the typed dates (effectiveFrom = computedRange ??
                // fromIso).
                state.setMultipleSearchParams({ from: f, to: t, tf_preset: 'custom' })
              }
            />
          </ControlZone>
          {compare && showComparison ? (
            <ControlZone testId="control-card-comparison">
              <ComparisonSwitch
                state={compare}
                rootOptions={state.kpiCascade.rootOptions}
                primaryRoot={state.kpiCascade.root}
                excludedDefaultRoots={compareExcludedRoots}
              />
            </ControlZone>
          ) : null}
          <ControlZone testId="control-card-dimensions" flex={1.2} minWidth={422}>
            <DimFilterPanel
              factTable={state.factTable}
              kpi={state.kpi}
              pageId={state.pageId}
              defaultGroupBy={state.groupByDefault}
              campaign={state.campaign}
            />
          </ControlZone>
        </ControlBand>

        {state.prunedDims ? (
          <Alert severity="info" onClose={state.dismissPrunedNotice}>
            {/* Campaigns C1 (spec §4): campaign-aware copy only when ALL pruned dims are the
                campaign's managed dims; a mixed prune keeps the generic copy naming every dim
                (honest superset). */}
            {state.campaign &&
            state.prunedDims.every((d) => (CAMPAIGN_MANAGED_DIMS as readonly string[]).includes(d))
              ? campaignPrunedNotice(state.campaign.label)
              : prunedDimsNotice(state.prunedDims)}
          </Alert>
        ) : null}
        {/* FU-31: a cross-page-carried kpi_root the page couldn't resolve was reset to the page
            default — one dismissible notice names it (backstop V6 noChartsForKpiNote stays). */}
        {state.removedKpi ? (
          <Alert severity="info" onClose={state.dismissRemovedKpiNotice}>
            {removedKpiNotice(state.removedKpi)}
          </Alert>
        ) : null}
        <StalenessIndicator />
        {state.timeframeReady ? (
          children
        ) : state.kpiCascade.controlsError ? null : state.dimCatalogError ? (
          <Alert severity="error">{MESSAGES.controlsLoadFailed}</Alert>
        ) : controls.isError ? (
          <Alert severity="error">{MESSAGES.controlsLoadFailed}</Alert>
        ) : state.ratioComponentControlsError ? (
          // C3b M1: a ratio page's component-fact controls settled in error under an active campaign
          // — the page fact's own controls succeeded, so surface the real error loudly here instead
          // of hanging on the misleading "no date range" warning below (readiness never lifts).
          <Alert severity="error">{MESSAGES.controlsLoadFailed}</Alert>
        ) : controls.isSuccess ? (
          <Alert severity="warning">{MESSAGES.noDateRangeForFact}</Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
      </Stack>
    </Box>
  )
}
